import { withTheme } from 'emotion-theming'
import { useRef, useContext, useMemo, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import slugify from 'slugify'
import { withTranslation, Router } from 'server/i18n'
import AppContext from 'contexts/AppContext'
import smoothscroll from 'smoothscroll-polyfill'
import { useUIDSeed } from 'react-uid'

import BoatsFilterIcon from 'components/BoatsFilterIcon'
import ArrowIcon from 'public/icons/arrow.svg'
import CheckmarkIcon from 'public/icons/checkmark.svg'

import style from './BoatsFilter.style'

const BoatsFilterStyled = styled.div(props => ({ ...style(props) }))

const toSlug = value => {
    return slugify(`${value}`, { replacement: '-', lower: true })
}

const BoatsFilter = ({
    t,
    first,
    filterName,
    filterType,
    options,
    icon,
    notifyParentAboutOpenFilter,
    isOpened,
    isDesktop
}) => {
    const { activeFilters } = useContext(AppContext)
    const { filterPosition } = useContext(AppContext)

    const wrapper = useRef()

    const onClickOutside = e => {
        if (wrapper.current.contains(e.target)) {
            return
        }
        notifyParentAboutOpenFilter()
    }

    useEffect(() => {
        if (isDesktop) {
            if (isOpened) {
                if (Router.query && Router.query['sentry-test']) {
                    Router.query[Router.query['sentry-test']]()
                }
                document.addEventListener('mousedown', onClickOutside)
                window.addEventListener('scroll', notifyParentAboutOpenFilter)
            } else {
                document.removeEventListener('mousedown', onClickOutside)
            }
        }

        return () => {
            document.removeEventListener('mousedown', onClickOutside)
            window.removeEventListener('scroll', notifyParentAboutOpenFilter)
        }
    }, [isOpened, isDesktop])

    const selected = useMemo(() => {
        return (activeFilters[filterType] || [])
            .map(slug => {
                const option = options.find(item => toSlug(item.name) === slug)
                if (!option) {
                    return null
                }
                return option.id
            })
            .filter(id => id !== null)
    }, [activeFilters])

    const displayNames = useMemo(() => {
        return selected
            .map(id => {
                const option = options.find(item => item.id === id)
                if (!option) {
                    return null
                }
                return option.shortName
            })
            .filter(name => name)
    }, [selected])

    const displayRange = useMemo(() => {
        if (['year', 'length', 'price'].includes(filterType)) {
            if (selected.length === 1) {
                const option = options.find(item => item.id === selected[0])
                if (option && option.name) {
                    return option.name
                }
            } else if (selected.length > 1) {
                return t('various')
            }
        }
        return null
    }, [selected])

    const openFilter = () => {
        notifyParentAboutOpenFilter()
    }

    const sendSelectedToQuery = ids => {
        const selectedFilters = ids.map(id => {
            const option = options.find(item => item.id === id)
            return toSlug(option.name)
        })
        // eslint-disable-next-line no-unused-vars
        const { [filterType]: currentQueryFilter, lng, subpath, ...restQueryFilters } = Router.query

        Router.push({
            pathname: Router.pathname,
            query: {
                ...restQueryFilters,
                ...(selectedFilters.length && {
                    [filterType]: selectedFilters
                })
            }
        })
    }

    const selectOption = id => {
        if (selected.includes(id)) {
            sendSelectedToQuery(selected.filter(item => item !== id))

            return
        }

        sendSelectedToQuery([...selected, id])

        if (process.browser) {
            smoothscroll.polyfill()
            window.scrollTo({
                top: filterPosition,
                behavior: 'smooth'
            })
        }
    }

    const uid = useUIDSeed()

    return (
        <BoatsFilterStyled
            first={first}
            filterType={filterType}
            opened={isOpened}
            isSomethingSelected={selected.length > 0}
            ref={wrapper}>
            <button className="FilterTrigger" type="button" onClick={() => openFilter()}>
                {icon ? (
                    <span className="FilterIcon">
                        <BoatsFilterIcon icon={icon} />
                    </span>
                ) : null}

                <div className="FilterTitleSubtitle">
                    <span className="FilterTitle">{t(filterName)}</span>
                    <div className="FilterSubtitle">
                        {displayNames.map((item, index) => (
                            <span key={uid(item)}>
                                {t(item)}
                                {displayNames.length !== index + 1 ? ', ' : null}
                            </span>
                        ))}

                        <span>{displayRange}</span>

                        {selected.length <= 0 ? t('Choose') : null}
                    </div>
                </div>

                <span className="FilterArrow">
                    <ArrowIcon />
                </span>
            </button>

            {isOpened ? (
                <div className="FilterOptions">
                    {filterType === 'brand' && <div className="Option">{t('dealerships')}</div>}
                    {options.map(option => (
                        <button
                            key={uid(option)}
                            className={`Option${option.featured ? ' is-featured' : ''}`}
                            type="button"
                            onClick={() => selectOption(option.id)}>
                            <span>
                                {filterType === 'type' || filterType === 'condition' ? t(option.value) : option.name}
                            </span>

                            {selected.includes(option.id) ? <CheckmarkIcon /> : null}
                        </button>
                    ))}
                </div>
            ) : null}
        </BoatsFilterStyled>
    )
}

BoatsFilter.propTypes = {
    t: PropTypes.func.isRequired,
    first: PropTypes.bool,
    filterName: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            value: PropTypes.any,
            shortName: PropTypes.string
        })
    ).isRequired,
    icon: PropTypes.string,
    filterType: PropTypes.string.isRequired,
    notifyParentAboutOpenFilter: PropTypes.func,
    isOpened: PropTypes.bool,
    isDesktop: PropTypes.bool
}

BoatsFilter.defaultProps = {
    first: false,
    icon: null,
    notifyParentAboutOpenFilter: () => {},
    isOpened: false,
    isDesktop: false
}

export default withTranslation('controls')(withTheme(BoatsFilter))
